/**
 * Bidirectional postMessage bridge between this UI (running as an
 * iframe) and its host application. All events are namespaced under a
 * single wire envelope so unrelated postMessage traffic on the parent
 * window is ignored cleanly.
 *
 * Wire format:
 *   { source: 'copilot-ui', type: <BridgeEventType>, payload?: unknown }
 *
 * The host is expected to mirror this envelope with `source: 'copilot-host'`
 * on inbound messages.
 */

import { isInIframe } from './iframe';
import { isPreProdEnvironment, resolveStage, Stage } from './environment';
import { ShoulderTapItem, ShoulderTapPayload } from '../services/api';
import {
  ShoulderTapFiredContent,
  ShoulderTapStaticContent,
} from '../content/shoulderTapContent';

export const BRIDGE_SOURCE_UI = 'copilot-ui';
export const BRIDGE_SOURCE_HOST = 'copilot-host';

export const BridgeEventType = {
  // UI → host
  ShoulderTapFired: 'shoulder-tap-fired',
  /**
   * The active tap sequence, on every change rather than only when a tap
   * fires. Lets a host rebuild its list after any fresh load of this iframe:
   * taps surfaced before that load won't be replayed by
   * `shoulder-tap-fired`, which fires once per tap.
   */
  ShoulderTapSequence: 'shoulder-tap-sequence',
  /**
   * Publishes the static copy bundle. Emitted on mount and in response to
   * `ContentRequest`, so hosts can render chrome (collapsed pill, offline
   * notice) before the first tap arrives.
   */
  CopilotContent: 'copilot-content',
  // host → UI
  ShoulderTapAccept: 'shoulder-tap-accept',
  ShoulderTapDismiss: 'shoulder-tap-dismiss',
  /**
   * Host asks us to (re-)publish the static copy bundle — for hosts that
   * attach their listener after we've mounted, or that remount their
   * overlay.
   */
  ContentRequest: 'content-request',
} as const;
export type BridgeEventType = typeof BridgeEventType[keyof typeof BridgeEventType];

/**
 * All user-facing copy for the shoulder-tap flow is owned by this UI and
 * travels on the wire (see `content/shoulderTapContent.ts`). Hosts render
 * from `content` and hold no copy of their own.
 *
 * `content` is additive to the pre-existing payload fields, so hosts that
 * haven't migrated yet keep working unchanged.
 */
export interface ShoulderTapFiredPayload {
  contactId: string;
  /** The tap that just fired. */
  tap: ShoulderTapPayload;
  /**
   * Every tap in the contact's active lifecycle (all taps since the last
   * dismiss), oldest first, including ones surfaced before this host document
   * loaded. `content` is resolved over exactly this sequence. `id` is stable
   * per tap and safe as a list key.
   */
  taps: ShoulderTapItem[];
  /**
   * True once any tap in the active lifecycle has been accepted. Contact-
   * scoped, not per-tap.
   */
  accepted: boolean;
  /**
   * Copy resolved over the whole tap sequence, not just `tap`. Render these
   * values from the latest event — a later tap can change the script.
   */
  content: ShoulderTapFiredContent;
}

/**
 * The active tap sequence as of the latest poll. There's no `tap` field because
 * nothing fired: refresh the list from this, don't raise an alert. An empty
 * `taps` means there is nothing to show — either the taps were dismissed, or
 * this contact never had any — so render no alert either way.
 */
export interface ShoulderTapSequencePayload {
  contactId: string;
  /** Same sequence and semantics as `ShoulderTapFiredPayload.taps`. */
  taps: ShoulderTapItem[];
  /** Same semantics as `ShoulderTapFiredPayload.accepted`. */
  accepted: boolean;
  /** Resolved over `taps`, exactly as on a fired event. */
  content: ShoulderTapFiredContent;
}

export interface CopilotContentPayload {
  content: ShoulderTapStaticContent;
}

interface BridgeEnvelope<T = unknown> {
  source: typeof BRIDGE_SOURCE_UI | typeof BRIDGE_SOURCE_HOST;
  type: BridgeEventType;
  payload?: T;
}

/**
 * Origins permitted to send us inbound events, keyed by our own stage.
 *
 * Two host applications embed this UI:
 *   - CCAT   — `cs.wolfgang.a2z.com`, with `beta.` / `gamma.` prefixes below
 *              prod (mirrors `urlPrefixByStage` in CCHUB's `utils/utils.ts`,
 *              which builds the CCAT deep links).
 *   - CCHUB  — runs as a micro-frontend inside the PHIRE UI container, so the
 *              embedding origin is the *container's*, not CCHUB's own. Prod and
 *              gamma are us-east-1; beta/alpha are us-west-2 (see
 *              PhireUIContainerCDK `lib/pipeline/stage.ts` and
 *              `lib/constants/domain.ts`).
 *
 * Scheme + host only. `event.origin` never carries a path, so an entry like
 * `https://…/customer-care-hub` could never match.
 *
 * NOTE: this list is keyed by *our* stage, and it is enforced in every
 * environment where it is non-empty. Adding a prod entry without the matching
 * beta/gamma ones would break the lower stages, which is why every deployed
 * stage is populated.
 */
const HOST_ORIGINS_BY_STAGE: Record<Stage, readonly string[]> = {
  prod: [
    'https://cs.wolfgang.a2z.com',
    // The container's CDK aliases prod to the apex (`getRegionalDomainName`
    // returns PHIRE_ROOT_DOMAIN for Prod), but the regional record is also
    // reported to serve. Both are listed because they are distinct origins,
    // and only the one the document actually loads from will ever post.
    // TODO: confirm which hostname agents land on and drop the other.
    'https://tools.pharmacy.amazon.dev',
    'https://us-east-1.tools.pharmacy.amazon.dev',
  ],
  gamma: [
    'https://gamma.cs.wolfgang.a2z.com',
    'https://us-east-1.gamma.tools.pharmacy.amazon.dev',
  ],
  beta: [
    'https://beta.cs.wolfgang.a2z.com',
    'https://us-west-2.beta.tools.pharmacy.amazon.dev',
  ],
  alpha: [
    // CCAT has no alpha of its own; CCHUB's stage map points alpha at beta.
    'https://beta.cs.wolfgang.a2z.com',
    'https://us-west-2.alpha.tools.pharmacy.amazon.dev',
  ],
  // Local and personal dev stacks are embedded by whatever the developer is
  // running (localhost, a personal container stack). Empty deliberately: an
  // empty list means "accept any origin", which applies to pre-prod only.
  dev: [],
};

/**
 * Origins allowed to send us inbound events. Empty = accept any origin, which
 * covers local/dev and is rejected outright in prod.
 *
 * A function rather than a const: the stage derives from `window.location`,
 * which isn't stable at module-load time.
 */
export const getAllowedHostOrigins = (): readonly string[] =>
  HOST_ORIGINS_BY_STAGE[resolveStage()];

const isBridgeEnvelope = (data: unknown): data is BridgeEnvelope => {
  if (!data || typeof data !== 'object') return false;
  const envelope = data as Partial<BridgeEnvelope>;
  return envelope.source === BRIDGE_SOURCE_HOST && typeof envelope.type === 'string';
};

const isOriginAllowed = (origin: string): boolean => {
  const allowed = getAllowedHostOrigins();
  if (allowed.length === 0) {
    // Open-origin fallback is only for dev/stub. In production, an
    // empty allowlist is a misconfiguration — reject inbound events
    // rather than accepting arbitrary parents that could trigger
    // accept/dismiss API calls with the current contactId.
    if (!isPreProdEnvironment()) {
      console.error('[hostBridge] No allowed origins configured in production — rejecting');
      return false;
    }
    return true;
  }
  if (allowed.includes(origin)) return true;
  // Loud on purpose: a host origin that drifts (new region, new stage, an
  // unexpected redirect) makes accept/dismiss silently stop working while the
  // alert still renders as though nothing were wrong.
  console.error(`[hostBridge] Rejected inbound event from unallowed origin: ${origin}`);
  return false;
};

/**
 * Emit an event to the host window. No-op when we're not inside an
 * iframe (e.g. running the app standalone in dev).
 */
export const postToHost = <T = unknown>(type: BridgeEventType, payload?: T): void => {
  if (!isInIframe()) return;
  const envelope: BridgeEnvelope<T> = { source: BRIDGE_SOURCE_UI, type, payload };
  // TODO: replace '*' with a captured host origin (handshake — the
  // host's first inbound message provides a browser-attested
  // event.origin we can pin outbound messages to).
  window.parent.postMessage(envelope, '*');
};

/**
 * Subscribe to a specific bridge event coming from the host. Returns
 * an unsubscribe function.
 */
export const subscribeFromHost = <T = unknown>(
  type: BridgeEventType,
  handler: (payload: T | undefined, event: MessageEvent) => void,
): (() => void) => {
  const listener = (event: MessageEvent) => {
    if (!isOriginAllowed(event.origin)) return;
    if (!isBridgeEnvelope(event.data)) return;
    if (event.data.type !== type) return;
    handler(event.data.payload as T | undefined, event);
  };
  window.addEventListener('message', listener);
  return () => window.removeEventListener('message', listener);
};
